type TetrominoType = 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L'

interface Position {
  x: number
  y: number
}

interface Tetromino {
  type: TetrominoType
  shape: number[][]
  pos: Position
}

export interface GameConfig {
  canvas: HTMLCanvasElement
  onScoreChange: (score: number) => void
  onLinesChange: (lines: number) => void
  onGameOver: (finalScore: number) => void
}

const SHAPES: Record<TetrominoType, number[][][]> = {
  I: [[[1, 1, 1, 1]], [[1], [1], [1], [1]]],
  O: [
    [
      [1, 1],
      [1, 1],
    ],
  ],
  T: [
    [
      [0, 1, 0],
      [1, 1, 1],
    ],
    [
      [1, 0],
      [1, 1],
      [1, 0],
    ],
    [
      [1, 1, 1],
      [0, 1, 0],
    ],
    [
      [0, 1],
      [1, 1],
      [0, 1],
    ],
  ],
  S: [
    [
      [0, 1, 1],
      [1, 1, 0],
    ],
    [
      [1, 0],
      [1, 1],
      [0, 1],
    ],
  ],
  Z: [
    [
      [1, 1, 0],
      [0, 1, 1],
    ],
    [
      [0, 1],
      [1, 1],
      [1, 0],
    ],
  ],
  J: [
    [
      [1, 0, 0],
      [1, 1, 1],
    ],
    [
      [1, 1],
      [1, 0],
      [1, 0],
    ],
    [
      [1, 1, 1],
      [0, 0, 1],
    ],
    [
      [0, 1],
      [0, 1],
      [1, 1],
    ],
  ],
  L: [
    [
      [0, 0, 1],
      [1, 1, 1],
    ],
    [
      [1, 0],
      [1, 0],
      [1, 1],
    ],
    [
      [1, 1, 1],
      [1, 0, 0],
    ],
    [
      [1, 1],
      [0, 1],
      [0, 1],
    ],
  ],
}

const COLORS: Record<TetrominoType, string> = {
  I: '#00f5ff',
  O: '#ffeb3b',
  T: '#9c27b0',
  S: '#4caf50',
  Z: '#f44336',
  J: '#2196f3',
  L: '#ff9800',
}

export class Game {
  private ctx: CanvasRenderingContext2D
  private config: GameConfig

  // Grid
  private readonly COLS = 10
  private readonly ROWS = 20
  private readonly CELL_SIZE: number
  private grid: (TetrominoType | null)[][] = []

  // Game state
  private currentPiece: Tetromino | null = null
  private rotationIndex: number = 0
  private score: number = 0
  private lines: number = 0
  private isGameOver: boolean = false

  // Timing
  private lastDrop: number = 0
  private dropInterval: number = 800
  private animationId: number | null = null

  // Touch
  private touchStartX: number = 0
  private touchStartY: number = 0

  // Colors
  private readonly BG_COLOR = '#1a1a2e'
  private readonly GRID_COLOR = '#16213e'

  constructor(config: GameConfig) {
    this.config = config
    this.ctx = config.canvas.getContext('2d')!

    const rect = config.canvas.getBoundingClientRect()
    // Use visible viewport height to account for browser chrome
    const visibleHeight = window.visualViewport?.height || window.innerHeight
    const height = Math.min(rect.height, visibleHeight - 80)
    this.CELL_SIZE = Math.floor(Math.min(rect.width / this.COLS, height / this.ROWS))

    this.resize()
    this.reset()
    this.setupInput()
  }

  private resize(): void {
    const rect = this.config.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    // Use visible viewport height to account for browser chrome
    const visibleHeight = window.visualViewport?.height || window.innerHeight
    const height = Math.min(rect.height, visibleHeight - 80)

    this.config.canvas.width = rect.width * dpr
    this.config.canvas.height = height * dpr
    this.ctx.scale(dpr, dpr)
  }

  private reset(): void {
    this.grid = Array(this.ROWS)
      .fill(null)
      .map(() => Array(this.COLS).fill(null))
    this.score = 0
    this.lines = 0
    this.isGameOver = false
    this.dropInterval = 800
    this.rotationIndex = 0
    this.spawnPiece()
  }

  private spawnPiece(): void {
    const types: TetrominoType[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L']
    const type = types[Math.floor(Math.random() * types.length)]
    this.rotationIndex = 0

    this.currentPiece = {
      type,
      shape: SHAPES[type][0],
      pos: { x: Math.floor(this.COLS / 2) - 1, y: 0 },
    }

    if (this.checkCollision(this.currentPiece.pos, this.currentPiece.shape)) {
      this.gameOver()
    }
  }

  private setupInput(): void {
    // Keyboard
    window.addEventListener('keydown', (e) => {
      if (this.isGameOver) return

      switch (e.key) {
        case 'ArrowLeft':
        case 'a':
        case 'A':
          this.move(-1)
          e.preventDefault()
          break
        case 'ArrowRight':
        case 'd':
        case 'D':
          this.move(1)
          e.preventDefault()
          break
        case 'ArrowUp':
        case 'w':
        case 'W':
          this.rotate()
          e.preventDefault()
          break
        case 'ArrowDown':
        case 's':
        case 'S':
          this.softDrop()
          e.preventDefault()
          break
        case ' ':
          this.hardDrop()
          e.preventDefault()
          break
      }
    })

    // Touch - tap zones for easy mobile controls
    // Left third = move left, Right third = move right
    // Top half of middle = rotate, Bottom half = soft drop
    // Swipe down anywhere = hard drop
    const canvas = this.config.canvas

    canvas.addEventListener(
      'touchstart',
      (e) => {
        if (this.isGameOver) return
        const touch = e.touches[0]
        this.touchStartX = touch.clientX
        this.touchStartY = touch.clientY
        e.preventDefault()
      },
      { passive: false }
    )

    canvas.addEventListener(
      'touchend',
      (e) => {
        if (this.isGameOver) return
        const touch = e.changedTouches[0]
        const rect = canvas.getBoundingClientRect()

        const deltaX = touch.clientX - this.touchStartX
        const deltaY = touch.clientY - this.touchStartY

        // Swipe down = hard drop
        if (deltaY > 50 && Math.abs(deltaX) < 50) {
          this.hardDrop()
          e.preventDefault()
          return
        }

        // For taps, check position
        if (Math.abs(deltaX) < 20 && Math.abs(deltaY) < 20) {
          const tapX = touch.clientX - rect.left
          const tapY = touch.clientY - rect.top
          const thirdWidth = rect.width / 3

          if (tapX < thirdWidth) {
            // Left third - move left
            this.move(-1)
          } else if (tapX > thirdWidth * 2) {
            // Right third - move right
            this.move(1)
          } else {
            // Middle third - rotate or soft drop based on Y
            if (tapY < rect.height / 2) {
              this.rotate()
            } else {
              this.softDrop()
            }
          }
        }
        e.preventDefault()
      },
      { passive: false }
    )
  }

  private move(dir: number): void {
    if (!this.currentPiece) return

    const newPos = { x: this.currentPiece.pos.x + dir, y: this.currentPiece.pos.y }
    if (!this.checkCollision(newPos, this.currentPiece.shape)) {
      this.currentPiece.pos = newPos
    }
  }

  private rotate(): void {
    if (!this.currentPiece) return

    const shapes = SHAPES[this.currentPiece.type]
    const nextRotation = (this.rotationIndex + 1) % shapes.length
    const newShape = shapes[nextRotation]

    if (!this.checkCollision(this.currentPiece.pos, newShape)) {
      this.currentPiece.shape = newShape
      this.rotationIndex = nextRotation
    }
  }

  private softDrop(): void {
    if (!this.currentPiece) return

    const newPos = { x: this.currentPiece.pos.x, y: this.currentPiece.pos.y + 1 }
    if (!this.checkCollision(newPos, this.currentPiece.shape)) {
      this.currentPiece.pos = newPos
      this.score += 1
      this.config.onScoreChange(this.score)
    }
  }

  private hardDrop(): void {
    if (!this.currentPiece) return

    let dropDistance = 0
    while (
      !this.checkCollision({ x: this.currentPiece.pos.x, y: this.currentPiece.pos.y + 1 }, this.currentPiece.shape)
    ) {
      this.currentPiece.pos.y++
      dropDistance++
    }

    this.score += dropDistance * 2
    this.config.onScoreChange(this.score)
    this.lockPiece()
  }

  private checkCollision(pos: Position, shape: number[][]): boolean {
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const newX = pos.x + x
          const newY = pos.y + y

          if (newX < 0 || newX >= this.COLS || newY >= this.ROWS) {
            return true
          }

          if (newY >= 0 && this.grid[newY][newX]) {
            return true
          }
        }
      }
    }
    return false
  }

  private lockPiece(): void {
    if (!this.currentPiece) return

    const { shape, pos, type } = this.currentPiece

    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const gridY = pos.y + y
          if (gridY >= 0) {
            this.grid[gridY][pos.x + x] = type
          }
        }
      }
    }

    this.clearLines()
    this.spawnPiece()
  }

  private clearLines(): void {
    let linesCleared = 0

    for (let y = this.ROWS - 1; y >= 0; y--) {
      if (this.grid[y].every((cell) => cell !== null)) {
        this.grid.splice(y, 1)
        this.grid.unshift(Array(this.COLS).fill(null))
        linesCleared++
        y++
      }
    }

    if (linesCleared > 0) {
      const points = [0, 100, 300, 500, 800][linesCleared] || 0
      this.score += points
      this.lines += linesCleared
      this.config.onScoreChange(this.score)
      this.config.onLinesChange(this.lines)

      // Speed up
      this.dropInterval = Math.max(100, 800 - this.lines * 20)
    }
  }

  private gameOver(): void {
    this.isGameOver = true
    this.config.onGameOver(this.score)
  }

  private update(currentTime: number): void {
    if (this.isGameOver || !this.currentPiece) return

    if (currentTime - this.lastDrop > this.dropInterval) {
      const newPos = { x: this.currentPiece.pos.x, y: this.currentPiece.pos.y + 1 }

      if (this.checkCollision(newPos, this.currentPiece.shape)) {
        this.lockPiece()
      } else {
        this.currentPiece.pos = newPos
      }

      this.lastDrop = currentTime
    }
  }

  private render(): void {
    const rect = this.config.canvas.getBoundingClientRect()
    const topPadding = 70 // Space for score UI
    const offsetX = (rect.width - this.COLS * this.CELL_SIZE) / 2
    const offsetY = topPadding + (rect.height - topPadding - this.ROWS * this.CELL_SIZE) / 2

    // Clear
    this.ctx.fillStyle = this.BG_COLOR
    this.ctx.fillRect(0, 0, rect.width, rect.height)

    // Draw grid background
    this.ctx.fillStyle = this.GRID_COLOR
    this.ctx.fillRect(offsetX, offsetY, this.COLS * this.CELL_SIZE, this.ROWS * this.CELL_SIZE)

    // Draw grid lines
    this.ctx.strokeStyle = 'rgba(255,255,255,0.05)'
    this.ctx.lineWidth = 1
    for (let x = 0; x <= this.COLS; x++) {
      this.ctx.beginPath()
      this.ctx.moveTo(offsetX + x * this.CELL_SIZE, offsetY)
      this.ctx.lineTo(offsetX + x * this.CELL_SIZE, offsetY + this.ROWS * this.CELL_SIZE)
      this.ctx.stroke()
    }
    for (let y = 0; y <= this.ROWS; y++) {
      this.ctx.beginPath()
      this.ctx.moveTo(offsetX, offsetY + y * this.CELL_SIZE)
      this.ctx.lineTo(offsetX + this.COLS * this.CELL_SIZE, offsetY + y * this.CELL_SIZE)
      this.ctx.stroke()
    }

    // Draw locked pieces
    for (let y = 0; y < this.ROWS; y++) {
      for (let x = 0; x < this.COLS; x++) {
        const cell = this.grid[y][x]
        if (cell) {
          this.drawCell(offsetX + x * this.CELL_SIZE, offsetY + y * this.CELL_SIZE, COLORS[cell])
        }
      }
    }

    // Draw ghost piece
    if (this.currentPiece) {
      let ghostY = this.currentPiece.pos.y
      while (!this.checkCollision({ x: this.currentPiece.pos.x, y: ghostY + 1 }, this.currentPiece.shape)) {
        ghostY++
      }

      this.ctx.globalAlpha = 0.3
      for (let y = 0; y < this.currentPiece.shape.length; y++) {
        for (let x = 0; x < this.currentPiece.shape[y].length; x++) {
          if (this.currentPiece.shape[y][x]) {
            this.drawCell(
              offsetX + (this.currentPiece.pos.x + x) * this.CELL_SIZE,
              offsetY + (ghostY + y) * this.CELL_SIZE,
              COLORS[this.currentPiece.type]
            )
          }
        }
      }
      this.ctx.globalAlpha = 1

      // Draw current piece
      for (let y = 0; y < this.currentPiece.shape.length; y++) {
        for (let x = 0; x < this.currentPiece.shape[y].length; x++) {
          if (this.currentPiece.shape[y][x]) {
            this.drawCell(
              offsetX + (this.currentPiece.pos.x + x) * this.CELL_SIZE,
              offsetY + (this.currentPiece.pos.y + y) * this.CELL_SIZE,
              COLORS[this.currentPiece.type]
            )
          }
        }
      }
    }
  }

  private drawCell(x: number, y: number, color: string): void {
    const padding = 2
    this.ctx.fillStyle = color
    this.ctx.beginPath()
    this.ctx.roundRect(x + padding, y + padding, this.CELL_SIZE - padding * 2, this.CELL_SIZE - padding * 2, 4)
    this.ctx.fill()

    // Highlight
    this.ctx.fillStyle = 'rgba(255,255,255,0.2)'
    this.ctx.fillRect(x + padding, y + padding, this.CELL_SIZE - padding * 2, 4)
  }

  private gameLoop = (currentTime: number): void => {
    this.update(currentTime)
    this.render()

    if (!this.isGameOver) {
      this.animationId = requestAnimationFrame(this.gameLoop)
    } else {
      this.animationId = null
    }
  }

  start(): void {
    this.lastDrop = performance.now()
    this.animationId = requestAnimationFrame(this.gameLoop)
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  restart(): void {
    this.reset()
    this.config.onScoreChange(0)
    this.config.onLinesChange(0)
    this.lastDrop = performance.now()
    if (this.animationId === null) {
      this.animationId = requestAnimationFrame(this.gameLoop)
    }
  }

  handleResize(): void {
    this.resize()
  }
}
